import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { MicrosoftTeamsLogo, PaperPlaneRight, CheckCircle, XCircle, Info } from "@phosphor-icons/react";
import { humanTime } from "@/lib/utils";
import { toast } from "sonner";

const TRIGGERS = [
  { key: "new_p1", label: "New P1 incident" },
  { key: "new_p2", label: "New P2 incident" },
  { key: "sla_breach", label: "SLA breach" },
  { key: "assignment", label: "Ticket assignment" },
  { key: "change_approval", label: "Change approval" },
  { key: "mail_poll_down", label: "Email pull down / recovered" },
];

const STATUS_STYLE = { sent: "text-emerald-700 dark:text-emerald-400", simulated: "text-sky-700 dark:text-sky-400", failed: "text-red-700 dark:text-red-400", skipped: "text-zinc-600 dark:text-zinc-400" };

export default function TeamsIntegration() {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadLog = () => api.get("/integrations/teams/log").then(r => setLog(r.data));
  useEffect(() => { api.get("/integrations/teams").then(r => setCfg(r.data)); loadLog(); }, []);

  if (!cfg) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/integrations/teams", cfg);
      setCfg(data);
      toast.success("Teams settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/teams/test");
      if (data.status === "sent") toast.success("Test card sent to Teams");
      else if (data.status === "simulated") toast.info(data.detail);
      else toast.error(`Test failed: ${data.detail}`);
      loadLog();
    } catch (e) { toast.error("Test failed"); }
    finally { setTesting(false); }
  };

  return (
    <div className="p-6 max-w-4xl" data-testid="teams-view">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Integrations</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black flex items-center gap-3"><MicrosoftTeamsLogo size={30} className="text-primary" weight="fill" /> Microsoft Teams</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Post Adaptive Cards to a Teams channel when key events happen. Create an <span className="text-primary">Incoming Webhook</span> via the Teams <span className="font-mono">Workflows</span> app ("When a Teams webhook request is received"), then paste the generated URL below.</p>
      </div>

      <Card className="p-6 border-border rounded-sm mb-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-lg">Enable notifications</div>
            <div className="text-xs text-muted-foreground">Master switch for all outbound Teams messages.</div>
          </div>
          <Switch data-testid="teams-enabled" checked={cfg.enabled} onCheckedChange={(v)=>setCfg({...cfg, enabled: v})} />
        </div>

        <div>
          <Label className="text-xs uppercase text-muted-foreground">Incoming Webhook URL</Label>
          <Input data-testid="teams-webhook" className="mt-1 rounded-sm font-mono text-xs" placeholder="https://prod-XX.westus.logic.azure.com:443/workflows/..." value={cfg.webhook_url || ""} onChange={(e)=>setCfg({...cfg, webhook_url: e.target.value})} />
          <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Info size={12} /> Treated as a secret. Leave blank to test in simulation mode.</div>
        </div>

        <div>
          <Label className="text-xs uppercase text-muted-foreground">Notify on</Label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {TRIGGERS.map(t => (
              <div key={t.key} className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                <span className="text-sm">{t.label}</span>
                <Switch data-testid={`teams-trigger-${t.key}`} checked={cfg.triggers?.[t.key] ?? true} onCheckedChange={(v)=>setCfg({...cfg, triggers: {...cfg.triggers, [t.key]: v}})} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <Button data-testid="teams-save" onClick={save} disabled={saving} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save Settings"}</Button>
          <Button data-testid="teams-test" onClick={sendTest} disabled={testing} variant="outline" className="rounded-sm font-mono uppercase text-xs border-primary/40"><PaperPlaneRight size={14} className="mr-2" />{testing ? "Sending…" : "Send Test"}</Button>
        </div>
      </Card>

      <Card className="border-border rounded-sm overflow-hidden" data-testid="teams-log-card">
        <div className="p-4 border-b border-border"><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Delivery Log</div></div>
        <div className="divide-y divide-border/60">
          {log.length === 0 && <div className="p-4 text-sm text-muted-foreground">No notifications sent yet.</div>}
          {log.map(l => (
            <div key={l.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
              {l.status === "sent" || l.status === "simulated" ? <CheckCircle size={14} className={STATUS_STYLE[l.status]} /> : <XCircle size={14} className={STATUS_STYLE[l.status] || "text-zinc-600 dark:text-zinc-400"} />}
              <span className="font-mono text-xs uppercase text-muted-foreground w-32">{l.event_type}</span>
              <span className={`text-xs font-mono ${STATUS_STYLE[l.status] || ""}`}>{l.status}</span>
              <span className="text-xs text-muted-foreground truncate flex-1">{l.detail}</span>
              <span className="text-[10px] font-mono text-muted-foreground">{humanTime(l.created_at)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
